import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Button, Col, Row } from '@storable/canaveral-components';
import { CONSUL, ROBOCERT, SSL_PROVIDERS } from '../../../constants/sslProviders';
import { validateDomain } from '../../../modules/domain';
import HostnameCertSettingsStatusPanelAlert from '../HostnameCertSettingsStatusPanelAlert';
import HostnameCertSettingsStatusPanelContent from '../../containers/HostnameCertSettingsStatusPanelContent';
import HostnameCertSettingsStatusPanelUpload from '../HostnameCertSettingsStatusPanelUpload';
import styles from './styles.scss';

const isValid = certificate => certificate?.validTo && Number(certificate.validTo) > Date.now();

const propTypes = {
  hostname: PropTypes.shape({
    hostname: PropTypes.string,
    id: PropTypes.string,
    primary: PropTypes.bool,
    ssl: PropTypes.bool.isRequired,
    sslType: PropTypes.oneOf(SSL_PROVIDERS).isRequired,
    DNSChangesRequired: PropTypes.bool.isRequired,
    DNSRecord: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
    }),
    certificate: PropTypes.shape({
      id: PropTypes.string,
      validTo: PropTypes.string,
    }),
  }).isRequired,
  handleSSLRequest: PropTypes.func.isRequired,
};

const HostnameCertSettingsStatusPanel = ({
  hostname,
  handleSSLRequest,
  SSLRequestErrors,
  SSLUploadSuccess,
}) => {
  const showNewSiteFlow = !hostname.ssl || !isValid(hostname.certificate);
  const isValidEntry = hostname?.hostname && validateDomain(hostname.hostname);
  const isNotAlreadyVoyager = hostname.websitePlatform !== 'voyager';
  const certReady = hostname.sslType === ROBOCERT && hostname.ssl;
  /*
      DNS Changes are required when:
        - Hostname entry is not blank and is a valid domain (isValidEntry)
        - Domain doesn't already point to voyager (isNotAlreadyVoyager)
        - Cert is ready in kubernetes (certReady)
   */
  const DNSChangesRequired = isValidEntry && isNotAlreadyVoyager && certReady;
  const showRequestCertButton = SSLRequestErrors || hostname.sslType === CONSUL;

  return (
    <div className={styles.statusPanel}>
      <Row>
        <HostnameCertSettingsStatusPanelAlert
          showNewSiteFlow={showNewSiteFlow}
          restartFlow={!!SSLRequestErrors}
        />
      </Row>
      <Row>
        <HostnameCertSettingsStatusPanelContent
          domain={hostname.hostname}
          DNSChangesRequired={DNSChangesRequired}
          SSLUploadSuccess={SSLUploadSuccess}
          certPending={hostname.sslType === ROBOCERT && !hostname.ssl}
          certReady={certReady}
        />
      </Row>
      <Row>
        <Col>
          <Button
            className={styles.button}
            context="neutral"
            onClick={window.location.reload}
          >
            Refresh
          </Button>
          {showRequestCertButton && (
            <Button
              className={styles.button}
              context="neutral"
              onClick={handleSSLRequest}
            >
              Request SSL Certificate
            </Button>
          )}
        </Col>
      </Row>
      <>
        <hr />
        <Row>
          <HostnameCertSettingsStatusPanelUpload hostname={hostname} />
        </Row>
      </>
    </div>
  );
};

HostnameCertSettingsStatusPanel.propTypes = propTypes;

HostnameCertSettingsStatusPanel.fragments = {
  certificate: gql`
    fragment HostnameCertSettingsStatusPanelCertificate on Certificate {
      validTo
    }
  `,
};

export default HostnameCertSettingsStatusPanel;
